/**
 * Tenant-detail Payout statements tab: client-side status filter + record.
 * TMMPDM-0037: the filter gains Charged; the caption links to the reseller's Settlements tab.
 * TMMPDM-0035: the status toggle lives in the URL (`status`, beside `?tab=`; default Pending).
 * TMMPDM-0095 / 0096: when the old payout actions are retired it is frozen history: a banner (no link:
 * the rows are several payees; each affiliate row links its own Ledger) and no Record payout or
 * Release hold.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { ErrorState, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { PAYOUT_STATUSES, type PayoutStatementView, type PayoutStatus } from '@/api/tmm/types';
import { RunSheetTable } from '../payouts/RunSheetTable';
import { RecordPayoutDialog } from '../payouts/RecordPayoutDialog';
import { FrozenHistoryBanner } from '../ledger/ledgerShared';
import { usePayoutActionsRetired } from '../ledger/ledger';

type PayoutStatementsView = { status: PayoutStatus };
const VIEW: UrlSchema<PayoutStatementsView> = {
  status: { default: 'PENDING', parse: oneOf(PAYOUT_STATUSES) },
};

interface PayoutStatementsPanelProps {
  slug: string;
  resellerId: number | null;
  resellerName: string | null;
}

export function PayoutStatementsPanel({
  slug,
  resellerId,
  resellerName,
}: PayoutStatementsPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<PayoutStatementView[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [recording, setRecording] = useState<PayoutStatementView | null>(null);
  // TMMPDM-0044: a payout the server refused — said here once the dialog closes and the list re-reads.
  const [refusal, setRefusal] = useState<string | null>(null);
  const payoutActions = usePayoutActionsRetired();
  const frozen = payoutActions.retired;
  const movedToLedger = () => {
    payoutActions.reload();
    setReloadToken((n) => n + 1);
  };

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listPayoutStatements(slug)
      .then((list) => {
        if (cancelled) return;
        setRows(list.payoutStatements);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  const filtered = rows === null ? null : rows.filter((row) => row.payoutStatus === status);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('payouts.loadError', 'Could not load the payout run sheet.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Box>
      {frozen ? <FrozenHistoryBanner /> : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {resellerId !== null ? (
          <>
            {t('payouts.tenantCaptionPrefix', "Payouts go to the tenant's reseller — see ")}
            <Link
              component={RouterLink}
              to={`/partners/${resellerId}?tab=settlements`}
              underline="always"
            >
              {t('payouts.tenantCaptionReseller', "{{resellerName}}'s Settlements", {
                resellerName: resellerName ?? `#${resellerId}`,
              })}
            </Link>
            {t(
              'payouts.tenantCaptionSuffix',
              '. Statements listed here are from before that change, and affiliate statements.',
            )}
          </>
        ) : (
          t(
            'payouts.tenantCaptionNoReseller',
            "Payouts go to the tenant's reseller once one is assigned. Statements listed here are from before that change, and affiliate statements.",
          )
        )}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={status}
          onChange={(_event, value: PayoutStatus | null) => {
            if (value) setView({ status: value });
          }}
          aria-label={t('payouts.status.groupAria', 'Payout status')}
        >
          {PAYOUT_STATUSES.map((value) => (
            <ToggleButton key={value} value={value}>
              {t(`payouts.status.${value}`, value)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {refusal ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRefusal(null)}>
          {refusal}
        </Alert>
      ) : null}

      <RunSheetTable
        rows={filtered}
        loading={rows === null}
        locale={i18n.language}
        emptyLabel={t('payouts.empty', 'No payout statements.')}
        onClearFilters={status === 'PENDING' ? undefined : () => setView({ status: 'PENDING' })}
        onRecord={setRecording}
        onHoldReleased={() => {
          setRefusal(null);
          setReloadToken((n) => n + 1);
        }}
        frozen={frozen}
        onMovedToLedger={movedToLedger}
      />

      <RecordPayoutDialog
        open={recording !== null}
        row={recording}
        onClose={() => setRecording(null)}
        onRecorded={() => {
          setRecording(null);
          setRefusal(null);
          notify.success(t('payouts.record.recorded', 'Payout recorded.'));
          setReloadToken((n) => n + 1);
        }}
        onRefused={(message) => {
          setRecording(null);
          setRefusal(message);
          setReloadToken((n) => n + 1);
        }}
        onMovedToLedger={movedToLedger}
      />
    </Box>
  );
}
